import { getClientIp } from 'request-ip'
import type { NextApiRequest } from 'next'

interface ErrorMessageItem {
  message: string
  path: string
  errorCode: string
}

export interface ErrorResponse {
  spanId: string
  traceId: string
  status: number
  timestamp: number
  message: string
  items: ErrorMessageItem[]
  errorCode: string
}

export interface FetchResponse<T = any> extends Response {
  data: T
  status: number
}

export type FetchGenericResponse<T, E = any> = SuccessfulResponse<T> | FailedResponse<E> | FetchResponse<T>
export type SuccessfulResponse<T> = FetchResponse<T> & { status: 200 | 201 | 202 | 204 | 206 }
export type ReturnedErrorResponse<E> = FetchResponse<E> & { status: 400 | 404 | 403 | 500 }
export type FailedResponse<E = any> = ReturnedErrorResponse<E>

export const getHeader = (headers: any, key: string): string => (headers[key] ?? headers[key.toLowerCase()]) as string
// https://stackoverflow.com/a/43666082

export const getBase64Auth = (uid: string, sid: string): string =>
  // eslint-disable-next-line no-undef
  uid && sid ? btoa(uid + ':' + sid) : ''

export const getIPAddress = (request: NextApiRequest): string => getClientIp(request)

const typedFetch = async <T, E = ErrorResponse>(
  url: string,
  options?: RequestInit
): Promise<FetchGenericResponse<T, E>> => {
  try {
    const response = await fetch(url, options)
    const data = await response.json()
    if (!response.ok) {
      return { ...response, data, status: data?.status } as ReturnedErrorResponse<E>
    }
    return { ...response, data, status: 200 } as SuccessfulResponse<T>
  } catch (error) {
    return { status: 500, statusText: error } as FailedResponse<E>
  }
}

export const get = async <T, E = ErrorResponse>(
  url: string,
  options?: RequestInit
): Promise<FetchGenericResponse<T, E>> => {
  return await typedFetch(url, options)
}

export const put = async (url: string, payload: any, options?: RequestInit): Promise<Response> => {
  const putMethod = {
    ...options,
    method: 'PUT',
    body: JSON.stringify(payload)
  }
  return fetch(url, putMethod)
}

export const post = async (url: string, payload: any, options?: RequestInit): Promise<Response> => {
  const postMethod = {
    ...options,
    method: 'POST',
    body: JSON.stringify(payload)
  }
  return await fetch(url, postMethod)
}
